<template>
  <div class="settlements-page">
    <div class="page-header">
      <h2>🏦 تسویه‌های من</h2>
      <p class="subtitle">مشاهده تسویه‌های انجام شده برای سالن</p>
    </div>

    <!-- ========================================== -->
    <!-- آمار خلاصه -->
    <!-- ========================================== -->
    <div v-if="summary" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">💰</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_settlement) }}</span>
          <span class="stat-label">مجموع تسویه‌ها</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">✅</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_paid) }}</span>
          <span class="stat-label">واریز شده</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">⏳</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_pending) }}</span>
          <span class="stat-label">در انتظار واریز</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">📊</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_commission) }}</span>
          <span class="stat-label">کارمزد کسر شده</span>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- فیلترها -->
    <!-- ========================================== -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>وضعیت</label>
          <select v-model="filters.status" class="filter-select" @change="loadSettlements">
            <option value="">همه</option>
            <option value="pending">در انتظار واریز</option>
            <option value="paid">واریز شده</option>
            <option value="failed">ناموفق</option>
          </select>
        </div>
        <div class="filter-group">
          <label>از تاریخ</label>
          <data-picker v-model="filters.date_from" type="date"  />
        </div>
        <div class="filter-group">
          <label>تا تاریخ</label>
          <data-picker v-model="filters.date_to" type="date"  />
        </div>
        <button class="btn-filter" @click="loadSettlements">🔍 اعمال فیلتر</button>
        <button class="btn-reset" @click="resetFilters">⟳ پاک کردن</button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- لیست تسویه‌ها -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else-if="settlements.length === 0" class="empty-state">
      <div class="empty-icon">🏦</div>
      <h3>هیچ تسویه‌ای یافت نشد</h3>
      <p>تاکنون تسویه‌ای برای سالن شما ثبت نشده است.</p>
    </div>

    <div v-else>
      <!-- ===== دسکتاپ: جدول ===== -->
      <div class="desktop-table">
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>تعداد تراکنش</th>
            <th>کل بیعانه</th>
            <th>کارمزد</th>
            <th>مبلغ واریزی</th>
            <th>کد پیگیری</th>
            <th>تاریخ تسویه</th>
            <th>وضعیت</th>
            <th>عملیات</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in settlements" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td>{{ item.transaction_count }}</td>
            <td class="amount">{{ formatPrice(item.total_deposit) }}</td>
            <td class="commission">
              {{ formatPrice(item.commission_total) }}
              <small>({{ item.commission_percent }}%)</small>
            </td>
            <td class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</td>
            <td>
              <span v-if="item.tracking_code" class="tracking-code">{{ item.tracking_code }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ get_date(item.created_at) }}</td>
            <td>
                <span :class="['badge', statusClass(item.status)]">
                  {{ item.status_label }}
                </span>
            </td>
            <td>
              <button class="btn-detail" @click="showDetail(item)">👁️ جزئیات</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ===== موبایل: کارت ===== -->
      <div class="mobile-cards">
        <div v-for="(item, index) in settlements" :key="item.id" class="card-item">
          <div class="card-header">
            <span class="card-index">#{{ index + 1 }}</span>
            <span :class="['badge', statusClass(item.status)]">{{ item.status_label }}</span>
          </div>
          <div class="card-body">
            <div class="card-row"><span class="card-label">تعداد:</span><span>{{ item.transaction_count }}</span></div>
            <div class="card-row"><span class="card-label">کل بیعانه:</span><span class="amount">{{ formatPrice(item.total_deposit) }}</span></div>
            <div class="card-row"><span class="card-label">کارمزد:</span><span class="commission">{{ formatPrice(item.commission_total) }}</span></div>
            <div class="card-row"><span class="card-label">مبلغ واریزی:</span><span class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</span></div>
            <div class="card-row"><span class="card-label">کد پیگیری:</span><span>{{ item.tracking_code || '—' }}</span></div>
            <div class="card-row"><span class="card-label">تاریخ:</span><span>{{ get_date(item.created_at) }}</span></div>
          </div>
          <div class="card-actions">
            <button class="btn-detail" @click="showDetail(item)">👁️ جزئیات</button>
          </div>
        </div>
      </div>

      <!-- ===== صفحه‌بندی ===== -->
      <div v-if="pagination.total > 0" class="pagination">
        <button
            class="btn-page"
            :disabled="pagination.current_page <= 1"
            @click="handlePageChange(pagination.current_page - 1)"
        >
          قبلی
        </button>
        <span class="page-info">
          صفحه {{ pagination.current_page }} از {{ pagination.last_page }}
        </span>
        <button
            class="btn-page"
            :disabled="pagination.current_page >= pagination.last_page"
            @click="handlePageChange(pagination.current_page + 1)"
        >
          بعدی
        </button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- مودال جزئیات تسویه -->
    <!-- ========================================== -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>📋 جزئیات تسویه</h3>
          <button class="btn-close" @click="closeDetailModal">✕</button>
        </div>
        <div class="modal-body">
          <div v-if="detailLoading" class="loading-state">
            <div class="spinner"></div>
          </div>
          <div v-else-if="detail">
            <!-- اطلاعات تسویه -->
            <div class="detail-summary">
              <div class="detail-row">
                <span>تعداد تراکنش:</span>
                <strong>{{ detail.transaction_count }}</strong>
              </div>
              <div class="detail-row">
                <span>کل بیعانه:</span>
                <strong class="amount">{{ formatPrice(detail.total_deposit) }}</strong>
              </div>
              <div class="detail-row">
                <span>کارمزد ({{ detail.commission_percent }}%):</span>
                <strong class="commission">{{ formatPrice(detail.commission_total) }}</strong>
              </div>
              <div class="detail-row total">
                <span>مبلغ واریزی:</span>
                <strong class="settlement-amount">{{ formatPrice(detail.settlement_amount) }}</strong>
              </div>
              <div class="detail-row" v-if="detail.tracking_code">
                <span>کد پیگیری:</span>
                <strong>{{ detail.tracking_code }}</strong>
              </div>
            </div>
            <!-- ========================================== -->
            <!-- ✅ سهم پرسنل (با کمسیون) -->
            <!-- ========================================== -->
            <div v-if="detail.by_admin && detail.by_admin.length > 0" class="admin-shares">
              <h4 class="detail-title">👥 سهم پرسنل (پس از کسر کمسیون)</h4>

              <div class="admin-shares-list">
                <div
                    v-for="admin in detail.by_admin"
                    :key="admin.admin_id || 'unknown'"
                    class="admin-share-item"
                >
                  <!-- هدر پرسنل -->
                  <div class="admin-share-header">
                    <span class="admin-name">{{ admin.admin_name }}</span>
                    <span class="admin-count">{{ admin.count }} تراکنش</span>
                  </div>

                  <!-- خلاصه مالی -->
                  <div class="admin-share-summary">
                    <div class="share-row">
                      <span class="share-label">جمع ناخالص:</span>
                      <span class="share-value">{{ formatPrice(admin.total_amount) }}</span>
                    </div>
                    <div class="share-row commission">
                      <span class="share-label">کمسیون کسر شده:</span>
                      <span class="share-value">-{{ formatPrice(admin.total_commission) }}</span>
                    </div>
                    <div class="share-row net">
                      <span class="share-label">مبلغ نهایی پرسنل:</span>
                      <span class="share-value">{{ formatPrice(admin.net_amount) }}</span>
                    </div>
                  </div>

                  <!-- لیست تراکنش‌های این پرسنل -->
                  <div class="admin-transactions">
                    <div
                        v-for="t in admin.transactions"
                        :key="t.id"
                        class="admin-transaction-item"
                    >
                      <div class="transaction-row">
                        <span class="transaction-user">{{ t.user_name }}</span>
                        <span class="transaction-order">سفارش #{{ t.order_id }}</span>
                      </div>
                      <div class="transaction-row">
                        <span class="transaction-amount">ناخالص: {{ formatPrice(t.amount) }}</span>
                        <span class="transaction-commission">کمسیون: -{{ formatPrice(t.commission) }}</span>
                        <span class="transaction-net">نهایی: {{ formatPrice(t.net_amount) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- لیست تراکنش‌ها -->
            <h4 class="detail-title">تراکنش‌های این تسویه</h4>
            <div class="detail-transactions">
              <div v-for="t in detail.transactions" :key="t.id" class="transaction-item">
                <div class="transaction-row">
                  <span class="transaction-user">{{ t.user_name }}</span>
                  <span class="transaction-amount">{{ formatPrice(t.amount) }}</span>
                </div>
                <div class="transaction-row">
                  <span class="transaction-order">سفارش #{{ t.order_id }}</span>
                  <span class="transaction-date">{{ get_date(t.reservation_date) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeDetailModal">بستن</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SalonSettlements',

  data() {
    return {
      loading: false,
      settlements: [],
      summary: null,
      filters: {
        status: '',
        date_from: '',
        date_to: ''
      },
      pagination: {
        total: 0,
        per_page: 20,
        current_page: 1,
        last_page: 1
      },
      showDetailModal: false,
      detailLoading: false,
      detail: null
    };
  },

  mounted() {
    this.loadSettlements();
  },

  methods: {
    async loadSettlements() {
      this.loading = true;
      try {
        const params = {
          status: this.filters.status || undefined,
          date_from: this.filters.date_from || undefined,
          date_to: this.filters.date_to || undefined,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        };

        const { data } = await axios.get('/api/admin/settlements', { params:params});

        this.settlements = data.data || [];
        this.summary = data.summary || null;
        this.pagination = data.pagination || this.pagination;

      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در بارگذاری تسویه‌ها');
      } finally {
        this.loading = false;
      }
    },

    async showDetail(item) {
      this.showDetailModal = true;
      this.detailLoading = true;
      this.detail = null;

      try {
        const { data } = await axios.get('/api/admin/settlements/' + item.id);
        this.detail = data;
      } catch (error) {
        this.$message?.error('خطا در بارگذاری جزئیات');
      } finally {
        this.detailLoading = false;
      }
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.detail = null;
    },

    handlePageChange(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.loadSettlements();
    },

    resetFilters() {
      this.filters = { status: '', date_from: '', date_to: '' };
      this.pagination.current_page = 1;
      this.loadSettlements();
    },

    formatPrice(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(parseFloat(value)) + ' تومان';
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.getFullYear() + '/' +
          String(d.getMonth() + 1).padStart(2, '0') + '/' +
          String(d.getDate()).padStart(2, '0');
    },

    statusClass(status) {
      const map = {
        pending: 'badge-warning',
        paid: 'badge-success',
        failed: 'badge-danger'
      };
      return map[status] || 'badge-gray';
    }
  }
};
</script>

<style scoped>
.settlements-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.subtitle {
  margin: 4px 0 0 0;
  color: #909399;
  font-size: 14px;
}

/* ===== آمار ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  font-size: 28px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

/* ===== فیلترها ===== */
.filter-section {
  background: white;
  padding: 16px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-input,
.filter-select {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
}

.btn-filter {
  padding: 6px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-filter:hover {
  background: #66b1ff;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

/* ===== جدول ===== */
.desktop-table {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  overflow-x: auto;
}

.desktop-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 900px;
}

.desktop-table thead th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 12px 14px;
  text-align: right;
  border-bottom: 2px solid #e4e7ed;
}

.desktop-table tbody td {
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
}

.desktop-table tbody tr:hover td {
  background: #f5f7fa;
}

.amount {
  font-weight: 600;
  color: #2c3e50;
}

.commission {
  color: #f56c6c;
  font-size: 12px;
}

.settlement-amount {
  font-weight: bold;
  color: #67c23a;
}

.tracking-code {
  font-family: monospace;
  font-size: 12px;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
}

.text-muted {
  color: #c0c4cc;
}

/* ===== بج‌ها ===== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.badge-success { background: #f0f9eb; color: #67c23a; }
.badge-warning { background: #fdf6ec; color: #e6a23c; }
.badge-danger { background: #fef0f0; color: #f56c6c; }

/* ===== دکمه‌ها ===== */
.btn-detail {
  padding: 4px 14px;
  background: #ecf5ff;
  color: #409eff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-detail:hover {
  background: #409eff;
  color: white;
}

/* ===== حالت‌ها ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 10px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 10px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

/* ===== صفحه‌بندی ===== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.btn-page {
  padding: 6px 16px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-page:hover:not(:disabled) {
  background: #66b1ff;
}

.btn-page:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #606266;
}

/* ===== کارت‌های موبایل ===== */
.mobile-cards {
  display: none;
}

.card-item {
  background: white;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.card-index {
  font-weight: 600;
  color: #909399;
  font-size: 13px;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-row {
  display: flex;
  gap: 6px;
  font-size: 13px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 70px;
}

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.card-actions .btn-detail {
  flex: 1;
  padding: 8px;
  text-align: center;
}

/* ===== مودال ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 550px;
  width: 92%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #909399;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.detail-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: #606266;
}

.detail-row.total {
  border-top: 1px solid #e4e7ed;
  padding-top: 10px;
  margin-top: 6px;
}

.detail-row strong.amount { color: #2c3e50; }
.detail-row strong.commission { color: #f56c6c; }
.detail-row strong.settlement-amount { color: #67c23a; font-size: 16px; }

.detail-title {
  margin: 16px 0 10px 0;
  color: #2c3e50;
  font-size: 14px;
}

.detail-transactions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.transaction-item {
  background: white;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
}

.transaction-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
  padding: 2px 0;
}

.transaction-user { font-weight: 500; color: #2c3e50; }
.transaction-amount { font-weight: 600; color: #409eff; }
.transaction-order { color: #909399; }
.transaction-date { color: #909399; }

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.btn-secondary {
  padding: 8px 24px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:hover {
  background: #a6a9ad;
}

/* ===== ریسپانسیو ===== */
@media (max-width: 768px) {
  .settlements-page {
    padding: 12px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-input,
  .filter-select {
    width: 100%;
  }

  .btn-filter,
  .btn-reset {
    width: 100%;
    text-align: center;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .modal-content {
    width: 95%;
  }
}

/* ========================================== */
/* سهم پرسنل */
/* ========================================== */
.admin-shares {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.admin-shares-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-share-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
}

.admin-share-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.admin-name {
  font-weight: 700;
  color: #2c3e50;
  font-size: 14px;
}

.admin-count {
  font-size: 11px;
  color: #909399;
  background: #e8f0fe;
  padding: 2px 8px;
  border-radius: 10px;
}

/* ===== خلاصه مالی ===== */
.admin-share-summary {
  margin-bottom: 10px;
  padding: 8px 0;
}

.share-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 13px;
}

.share-label {
  color: #606266;
}

.share-value {
  font-weight: 600;
  color: #2c3e50;
}

.share-row.commission .share-value {
  color: #f56c6c;
}

.share-row.net {
  padding-top: 6px;
  margin-top: 4px;
  border-top: 1px dashed #e4e7ed;
}

.share-row.net .share-label {
  font-weight: 700;
  color: #2c3e50;
}

.share-row.net .share-value {
  font-size: 15px;
  font-weight: 800;
  color: #67c23a;
}

/* ===== تراکنش‌ها ===== */
.admin-transactions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px dashed #e4e7ed;
}

.admin-transaction-item {
  background: white;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
}

.transaction-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  flex-wrap: wrap;
}

.transaction-user {
  color: #2c3e50;
  font-weight: 500;
}

.transaction-order {
  color: #909399;
  font-size: 11px;
}

.transaction-amount {
  color: #606266;
  font-size: 11px;
}

.transaction-commission {
  color: #f56c6c;
  font-size: 11px;
}

.transaction-net {
  color: #409eff;
  font-weight: 600;
  font-size: 11px;
}
</style>